import { useMemo, useState } from "react";
import { RECRUITER_CATEGORIES, RECRUITER_DIRECTORY } from "../../lib/recruiterDirectory";

/**
 * A simple filterable list for now — search plus a category rail, no logos or
 * per-company detail. Laid out like the Faculty page's own shell (sticky rail
 * + content grid, the same search box and filter-button classes) rather than
 * a bespoke filter UI, so a 280px rail keeps the search box and the six
 * long category names to a sane width instead of stretching full-page.
 *
 * See recruiterDirectory.ts for the source and the two gaps carried from it.
 */
export function RecruiterDirectory() {
  const [category, setCategory] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return RECRUITER_DIRECTORY.filter(
      (r) =>
        (category === null || r.category === category) &&
        (needle === "" || r.name.toLowerCase().includes(needle)),
    );
  }, [category, q]);

  const grouped = useMemo(() => {
    const byCategory = new Map<string, typeof RECRUITER_DIRECTORY>();
    for (const c of RECRUITER_CATEGORIES) byCategory.set(c, []);
    for (const r of filtered) byCategory.get(r.category)?.push(r);
    return RECRUITER_CATEGORIES.map((c) => [c, byCategory.get(c) ?? []] as const).filter(
      ([, list]) => list.length > 0,
    );
  }, [filtered]);

  const hasFilters = category !== null || q.trim() !== "";

  return (
    <section className="section section-tight" aria-labelledby="recruiter-directory-h2">
      <div className="container">
        <div className="kicker">Who has recruited here</div>
        <h2 id="recruiter-directory-h2" style={{ fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>
          {RECRUITER_DIRECTORY.length} companies, 2022&ndash;2026.
        </h2>
        <p style={{ maxWidth: "62ch", color: "var(--text-soft)" }}>
          From the Training &amp; Placement Cell&rsquo;s own recruiter directory, six
          industry categories. Filter by category or search a name.
        </p>

        <div className="faculty-shell" style={{ marginTop: "var(--space-6)" }}>
          <aside className="faculty-rail" aria-label="Filter recruiters">
            <div className="faculty-rail-section">
              <label className="faculty-rail-label" htmlFor="recruiter-q">
                Search
              </label>
              <div className="faculty-search">
                <input
                  id="recruiter-q"
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Company name"
                />
              </div>
            </div>

            <div className="faculty-rail-section">
              <div className="faculty-rail-label">Category</div>
              <ul className="faculty-rail-list">
                <li>
                  <button
                    type="button"
                    className={`faculty-rail-btn${category === null ? " active" : ""}`}
                    onClick={() => setCategory(null)}
                  >
                    <span>
                      <span className="faculty-rail-name" style={{ whiteSpace: "normal", maxWidth: "none" }}>
                        All categories
                      </span>
                    </span>
                    <span className="faculty-rail-count">{RECRUITER_DIRECTORY.length}</span>
                  </button>
                </li>
                {RECRUITER_CATEGORIES.map((c) => {
                  const count = RECRUITER_DIRECTORY.filter((r) => r.category === c).length;
                  return (
                    <li key={c}>
                      <button
                        type="button"
                        className={`faculty-rail-btn${category === c ? " active" : ""}`}
                        onClick={() => setCategory(category === c ? null : c)}
                      >
                        <span>
                          {/* The category names are the source deck's own long
                              headings ("Product-Based / IT Product / IT Services /
                              Consulting") — .faculty-rail-name's shared ellipsis
                              truncation would clip them to the point of being
                              unreadable, so it's overridden here to wrap instead. */}
                          <span className="faculty-rail-name" style={{ whiteSpace: "normal", maxWidth: "none" }}>
                            {c}
                          </span>
                        </span>
                        <span className="faculty-rail-count">{count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="faculty-rail-foot">
              <span className="faculty-rail-tally">
                Showing <b>{filtered.length}</b> of {RECRUITER_DIRECTORY.length}
              </span>
              {hasFilters && (
                <button
                  type="button"
                  className="faculty-rail-clear"
                  onClick={() => {
                    setCategory(null);
                    setQ("");
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </aside>

          <div className="faculty-content">
            {filtered.length === 0 ? (
              <div className="faculty-empty">
                No company matches your filters. Try a different search term or clear filters.
              </div>
            ) : (
              grouped.map(([cat, list]) => (
                <div key={cat}>
                  <div className="faculty-dept-head">
                    <h3>{cat}</h3>
                    <span className="count">
                      {list.length} {list.length === 1 ? "company" : "companies"}
                    </span>
                  </div>
                  <ul
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px 10px",
                      padding: 0,
                      margin: 0,
                      listStyle: "none",
                    }}
                  >
                    {list.map((r) => (
                      <li
                        key={r.name}
                        style={{
                          border: "1px solid var(--rule)",
                          borderRadius: "var(--radius-1)",
                          padding: "6px 12px",
                          fontSize: "14px",
                          color: "var(--text)",
                          background: "var(--bg)",
                        }}
                      >
                        {r.name}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
